import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";

import {
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
} from "@mui/material";

import { ClearLines } from "components/EditPage/ClearLines";
import { LineTableHeadingCell } from "components/formFields/LineTableCell";
import { LineColumnFillHeading } from "components/formFields/line/LineColumnFillHeading";
import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { LineDragInsertCopy } from "components/formFields/line/LineDragInsertCopy";
import { LineInventoryItemHeading } from "components/formFields/line/LineInventoryItem";
import { LineItemFooter } from "components/formFields/line/LineItemFooter";
import { DragHandleHeading } from "components/utility/DragHandle";

import { i18n } from "services/i18nService";
import { afterTouchLine } from "services/sosInventoryService/purchasingTransaction/afterTouchLine";
import { expandPurchasingItemGroup } from "services/sosInventoryService/purchasingTransaction/domainLogic";
import { setPageDirty } from "services/utility/edit";
import { handleDrop, copyDown } from "services/utility/lineItems";

import { useJobWorkcenters } from "hooks/useJobWorkcenters";
import { usePrivileges } from "hooks/usePrivileges";
import { useRelated } from "hooks/useRelated";

import { LineItem } from "views/PurchaseTransactions/LineItem";

import globalState from "globalState/globalState";
import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

import { USER_PRIVILEGE } from "appConstants";
import { EMPTY_LINE_ITEM } from "editConfig";

export function LineItems(props) {
  const {
    objectType,
    record,
    lines,
    taxCodes,
    items,
    totals,
    itemsCount,
    addItem,
    classes,
    lineHandler,
    lineItemsErrors,
    setErrors,
    setClearLines,
    bins,
    relatedRecords,
    showAppliedTo,
    showBinSerialAndLot,
    showReceived,
  } = props;

  const customers = useRelated("customer");
  const jobWorkcenters = useJobWorkcenters();
  const { hasPrivilegesOrIsAdmin } = usePrivileges();
  const viewCosts = hasPrivilegesOrIsAdmin(USER_PRIVILEGE.viewCosts);

  // SETTINGS
  const showVolumeOnPurchases = useSelector(
    (state) => state.userCompanySettings.settings.showVolumeOnPurchases
  );
  const showWeightOnPurchases = useSelector(
    (state) => state.userCompanySettings.settings.showWeightOnPurchases
  );
  const classEnabled = useSelector(
    (state) => state.userCompanySettings.settings.classEnabled
  );
  const showCustomerOnPurchaseForms = useSelector(
    (state) => state.userCompanySettings.settings.showCustomerOnPurchaseForms
  );
  const jobEnabled = useSelector(
    (state) => state.userCompanySettings.settings.jobEnabled
  );
  const globalEnabled = useSelector(
    (state) => state.userCompanySettings.settings.globalEnabled
  );
  const uomEnabled = useSelector(
    (state) => state.userCompanySettings.settings.uomEnabled
  );

  function insertEmptyLine(insertAt) {
    setPageDirty();
    lineHandler({
      type: "insert",
      insertAt: insertAt,
      newLine: EMPTY_LINE_ITEM[objectType],
    });
  }

  async function expandItemGroup(lineToExpand) {
    globalState.dispatch(editModalLoadingIndicatorOn());

    const bomLineItems = await expandPurchasingItemGroup(lineToExpand, record);
    const afterTouchedLines = bomLineItems.map((line) =>
      afterTouchLine(line, "item")
    );
    setPageDirty();
    lineHandler({ type: "delete", deleteAt: lineToExpand.lineNumber });
    lineHandler({
      type: "insertMany",
      insertAt: lineToExpand.lineNumber,
      lines: afterTouchedLines,
    });

    globalState.dispatch(editModalLoadingIndicatorOff());
  }

  if (!lines) {
    return null;
  }

  return (
    <TableContainer data-testing="lineItems" sx={{ overflowX: "initial" }}>
      <Table size="small" padding="none" stickyHeader>
        <TableHead>
          <TableRow sx={{ verticalAlign: "bottom", lineHeight: "1" }}>
            <DragHandleHeading />
            <ClearLines setClearLines={setClearLines} />
            <LineTableHeadingCell>{i18n("columns.Line")}</LineTableHeadingCell>
            <LineInventoryItemHeading lineHandler={lineHandler} />
            <LineColumnHeading labelCode="Description" />
            <LineColumnHeading labelCode="PartNumber" />
            <LineColumnHeading labelCode="Quantity" />
            {uomEnabled && <LineColumnHeading labelCode="UOM" />}
            {showWeightOnPurchases && <LineColumnHeading labelCode="Weight" />}
            {showVolumeOnPurchases && <LineColumnHeading labelCode="Volume" />}
            {viewCosts && (
              <LineColumnHeading labelCode="UnitPrice" align="right" />
            )}
            {viewCosts && (
              <LineColumnHeading labelCode="Amount" align="right" />
            )}
            {showReceived && (
              <LineColumnHeading labelCode="Received" align="right" />
            )}
            {showAppliedTo && <LineColumnHeading labelCode="AppliedTo" />}
            {classEnabled && (
              <LineColumnFillHeading
                labelCode="Class"
                field="class"
                lineHandler={lineHandler}
              />
            )}
            {globalEnabled && (
              <LineColumnFillHeading
                labelCode="Tax"
                field="taxCode"
                lineHandler={lineHandler}
              />
            )}
            {showCustomerOnPurchaseForms && (
              <LineColumnFillHeading
                labelCode="Customer"
                field="customer"
                lineHandler={lineHandler}
              />
            )}
            {jobEnabled && (
              <LineColumnFillHeading
                labelCode="Job"
                field="jobWorkcenter"
                lineHandler={lineHandler}
              />
            )}
          </TableRow>
        </TableHead>
        <DragDropContext
          onDragEnd={(result) => handleDrop(result, lineHandler, lines)}
        >
          <Droppable droppableId="lineItems">
            {(provided) => (
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {lines.map((line, index) => (
                  <Draggable key={index} draggableId={`${index}`} index={index}>
                    {(draggableProvided, snapshot) => {
                      return (
                        <TableRow
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          sx={{
                            ...draggableProvided.draggableProps.style,
                            position: "inherit",
                            backgroundColor: snapshot.isDragging
                              ? "dragBackground"
                              : "none",
                          }}
                          data-testing={"line-" + index}
                        >
                          <LineDragInsertCopy
                            draggableProvided={draggableProvided}
                            snapshot={snapshot}
                            insertEmptyLine={insertEmptyLine}
                            lineNumber={line.lineNumber}
                            onCopyDown={() =>
                              copyDown(line, lineHandler, objectType)
                            }
                            showCopyDown={
                              line.lineNumber === lines.length ? false : true
                            }
                          />

                          <LineItem
                            objectType={objectType}
                            record={record}
                            line={line}
                            lineHandler={lineHandler}
                            expandItemGroup={expandItemGroup}
                            items={items}
                            itemsCount={itemsCount}
                            addItem={addItem}
                            taxCodes={taxCodes}
                            classes={classes}
                            customers={customers}
                            jobWorkcenters={jobWorkcenters}
                            bins={bins}
                            relatedRecords={relatedRecords}
                            lineItemsErrors={lineItemsErrors}
                            setErrors={setErrors}
                            draggableProvided={draggableProvided}
                            draggableSnapshot={snapshot}
                            showAppliedTo={showAppliedTo}
                            showBinSerialAndLot={showBinSerialAndLot}
                            showReceived={showReceived}
                          />
                        </TableRow>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
        <LineItemFooter
          showWeight={showWeightOnPurchases}
          showVolume={showVolumeOnPurchases}
          totals={totals}
          firstCellSpan={5}
          lineHandler={lineHandler}
          objectType={objectType}
        />
      </Table>
    </TableContainer>
  );
}
